import type { ColumnDef } from '@tanstack/react-table';
import type { BaseColumn } from 'types/table';
import { format } from 'date-fns';
import { t } from '@lingui/macro';
import { trans } from '../../utils/index';
import type { EditableState, CustomColumn } from '../types';
import { fieldTypeConfig, getFieldPlaceholder, renderColumnCell } from './columnConfig';

type ColumnOverrides<T> = Partial<ColumnDef<T>>;

// Extended column type that allows BaseColumn, CustomColumn, or custom ColumnDef
type ExtendedColumn<T> =
  | BaseColumn
  | CustomColumn<T>
  | (Partial<ColumnDef<T>> & { id: string; label: string });

type ExtendedColumns<T> = Record<string, ExtendedColumn<T>>;

type GenerateColumnsOptions<T> = {
  cols: ExtendedColumns<T>;
  customColumns?: ColumnDef<T>[];
  columnOverrides?: Partial<Record<string, ColumnOverrides<T>>>;
  dateFormatter?: (dateString: string | null) => string;
  editableState?: EditableState;
  setEditableState?: (state: EditableState) => void;
};

// Type guard to check if a column is a BaseColumn (has type property)
const isBaseColumn = <T,>(col: ExtendedColumn<T>): col is BaseColumn => {
  return 'type' in col && col.type !== undefined;
};

/**
 * Safely access nested properties using dot notation path
 * @example getNestedValue({ brand: { name: 'Nike' } }, 'brand.name') => 'Nike'
 */
const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
};

const defaultDateFormatter = (dateString: string | null) => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd-MMM-yyyy');
  } catch {
    return t`Invalid date`;
  }
};

/**
 * Generates table columns from BaseColumns configuration
 * Uses renderColumnCell for all column rendering
 *
 * Note: For complex column configurations (custom types, editable columns,
 * link columns, drawer columns), use createColumns from createColumns.tsx instead.
 * This function is used internally by createColumns for standard column generation.
 */
export const generateColumns = <T extends Record<string, unknown>>(
  options: GenerateColumnsOptions<T>
): ColumnDef<T>[] => {
  const {
    cols,
    customColumns = [],
    columnOverrides = {},
    dateFormatter = defaultDateFormatter,
  } = options;

  const generatedColumns: ColumnDef<T>[] = Object.entries(cols).map(([key, col]) => {
    // If it's a custom column (not a BaseColumn), use it directly
    if (!isBaseColumn(col)) {
      const customCol = col as Partial<ColumnDef<T>> & { id: string; label: string };
      const hasAccessorKey = 'accessorKey' in customCol && customCol.accessorKey;
      // Extract only needed properties, excluding id and label which are handled separately
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: unusedId, label: unusedLabel, ...restCol } = customCol;
      return {
        ...(hasAccessorKey
          ? { accessorKey: (customCol as { accessorKey?: string }).accessorKey }
          : { accessorKey: key }),
        id: col.id,
        header: (customCol.header as string | undefined) ?? col.label,
        ...restCol,
        ...columnOverrides[key],
      } as ColumnDef<T>;
    }

    // Standard BaseColumn - generate from config
    // Priority: accessorFn > accessorKey with dot notation > accessorKey > key
    // Use type assertion to access extended properties that may exist on CustomColumn
    const extendedCol = col as CustomColumn<T>;
    const accessorPath = extendedCol.accessorKey ?? key;
    const hasNestedPath = accessorPath.includes('.');

    // Determine the accessor to use
    let accessorConfig: { accessorKey?: string; accessorFn?: (row: T) => unknown };
    if (extendedCol.accessorFn) {
      // Use explicit accessorFn if provided (for computed/transformed values)
      accessorConfig = { accessorFn: extendedCol.accessorFn as (row: T) => unknown };
    } else if (col.type === 'dd') {
      // Dropdown columns: auto-extract .name from {id, name} objects
      accessorConfig = {
        accessorFn: (row: T) => {
          const value = (row as Record<string, unknown>)[accessorPath];
          if (value && typeof value === 'object' && 'name' in value) {
            return (value as { name: string }).name;
          }
          return value;
        },
      };
    } else if (hasNestedPath) {
      // Use accessorFn for nested paths (dot notation like 'brand.name')
      accessorConfig = {
        accessorFn: (row: T) => getNestedValue(row as Record<string, unknown>, accessorPath),
      };
    } else {
      // Use simple accessorKey for flat properties
      accessorConfig = { accessorKey: accessorPath };
    }

    const baseColumn: ColumnDef<T> = {
      ...accessorConfig,
      id: col.id,
      header: typeof col.label === 'string' ? (trans(col.label) as string) : col.label,
      ...columnOverrides[key],
    };

    // Apply cell rendering and sizes based on type using renderColumnCell
    if (!baseColumn.cell && col.type) {
      // Convert to CustomColumn format for renderColumnCell
      // Type assertion is safe because renderColumnCell only uses the column config
      // for rendering, not for calling onUpdate (which is handled by the renderer)
      const customCol = {
        ...col,
        id: col.id,
        type: col.type,
      } as unknown as CustomColumn<{ id: number | string }>;

      // Use renderColumnCell for all column rendering (editable state from context in cells)
      const rendered = renderColumnCell(customCol, {
        dateFormatter,
      });

      Object.assign(baseColumn, rendered);

      // Apply default sizes from fieldTypeConfig if not already set
      const typeConfig = fieldTypeConfig[col.type];
      if (typeConfig) {
        baseColumn.minSize = baseColumn.minSize ?? typeConfig.minSize;
        baseColumn.size = baseColumn.size ?? typeConfig.size;
        baseColumn.maxSize = baseColumn.maxSize ?? typeConfig.maxSize;
      }

      // Set placeholder in meta - use column's placeholder or generate default from type config
      const placeholder =
        extendedCol.placeholder ?? getFieldPlaceholder(col.type, String(col.label));
      if (placeholder) {
        baseColumn.meta = {
          ...baseColumn.meta,
          placeholder,
        };
      }
    }

    return baseColumn;
  });

  return [...generatedColumns, ...customColumns];
};
